import { useState, useRef, useEffect, useCallback } from 'react';
import { IressIcon } from '@iress-oss/ids-components';

type CopyState = 'idle' | 'copied' | 'error';

interface CopyButtonProps {
  /** Text to write to the clipboard when triggered. */
  text: string;
  /** Button label in the idle state. Defaults to "Copy". */
  label?: string;
}

/**
 * Self-contained clipboard copy button with inline 2-second feedback.
 *
 * - Idle:   clipboard icon + `label` (borderless, right-aligned via CSS).
 * - Success: tick icon + "Copied to clipboard" in navy (non-interactive div).
 * - Failure: "Failed to copy to clipboard" in red (non-interactive div).
 *
 * Styles are defined in ChatApp.css under the `.porter-copy` namespace and
 * apply wherever this component is used.
 */
export function CopyButton({ text, label = 'Copy' }: Readonly<CopyButtonProps>) {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending reset timer on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopyState('copied');
        timerRef.current = setTimeout(() => setCopyState('idle'), 2000);
      },
      () => {
        setCopyState('error');
        timerRef.current = setTimeout(() => setCopyState('idle'), 2000);
      },
    );
  }, [text]);

  if (copyState === 'copied') {
    return (
      <div className="porter-copy porter-copy--success">
        <span className="porter-copy__icon">
          <IressIcon name="check" />
        </span>{' '}
        Copied to clipboard
      </div>
    );
  }

  if (copyState === 'error') {
    return (
      <div className="porter-copy porter-copy--error">
        Failed to copy to clipboard
      </div>
    );
  }

  return (
    <button
      className="porter-copy porter-copy--idle"
      type="button"
      onClick={handleCopy}
      aria-label={label}
    >
      <span className="porter-copy__icon">
        <IressIcon name="content_copy" />
      </span>
      {label}
    </button>
  );
}
